import { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { MdLocationOn } from 'react-icons/md';
import GoogleMapReact from 'google-map-react';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import _ from 'lodash';
import Marker from '../../../../DisplayMap/Marker';
import StaticMapPreview from '../../../../DisplayMap/StaticMapPreview';
import useElementSize from '../../../../../hooks/useElementSize';
import readTourNotesText from '../../../../../helpers/readTourNotesText';
import { activateMap } from '../../../../../store/slices/tripSearch';
import { googleApiKey } from '../../../../../constants';

const CONTAINER_MEASURE_STEP = 20;
const MEETING_POINT_ZOOM = 14;

const getMeetingLocation = (itinerary, product) => {
  const latitude = product?.latitude ?? itinerary.tour?.latitude;
  const longitude = product?.longitude ?? itinerary.tour?.longitude;
  return {
    notes: readTourNotesText(product?.meetingNotes),
    latitude,
    longitude,
    hasCoordinates:
      _.isNumber(latitude) && _.isNumber(longitude) && latitude !== 0 && longitude !== 0,
  };
};

export const hasMeetingLocation = (itinerary, product) => {
  const location = getMeetingLocation(itinerary, product);
  return !!location.notes || location.hasCoordinates;
};

const MeetingLocationDetails = ({ itinerary, product }) => {
  const { notes, latitude, longitude, hasCoordinates } = getMeetingLocation(itinerary, product);
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const isMapActivated = useSelector((state) => state?.tripSearch?.isMapActivated);
  const { ref: containerRef, width, height } = useElementSize(CONTAINER_MEASURE_STEP);

  const handleActivate = useCallback(() => {
    track('map_activated', { view: 'tour_details', productId: itinerary.productId });

    dispatch(activateMap(undefined));
  }, [dispatch, track, itinerary.productId]);

  const renderMapSurface = () => {
    if (!isMapActivated) {
      if (!width || !height) {
        return null;
      }

      return (
        <StaticMapPreview
          initialViewport={{ center: { lat: latitude, lng: longitude }, zoom: MEETING_POINT_ZOOM }}
          initialWidth={width}
          initialHeight={height}
          onActivate={handleActivate}
        />
      );
    }

    return (
      <GoogleMapReact
        yesIWantToUseGoogleMapApiInternals
        bootstrapURLKeys={{ key: googleApiKey }}
        center={{ lat: latitude, lng: longitude }}
        defaultZoom={MEETING_POINT_ZOOM}
      >
        <Marker
          selected
          key={itinerary.productId}
          lat={latitude}
          lng={longitude}
          markerKey={itinerary.productId}
        />
      </GoogleMapReact>
    );
  };

  return (
    <div className="text-sm">
      {notes && (
        <div className="flex">
          <MdLocationOn size="16" className="text-gray-400 mr-2 flex-shrink-0 mt-1" />
          <div className="whitespace-pre-line">{notes}</div>
        </div>
      )}
      {hasCoordinates && (
        <div ref={containerRef} className="mt-3 w-full md:w-2/3" style={{ height: '20rem' }}>
          {renderMapSurface()}
        </div>
      )}
    </div>
  );
};

MeetingLocationDetails.propTypes = {
  itinerary: PropTypes.shape().isRequired,
  product: PropTypes.shape(),
};

MeetingLocationDetails.defaultProps = {
  product: undefined,
};

export default memo(MeetingLocationDetails);
